import { FileData, RequiredFiles, StudentDiagnosticReport } from '../types';
import { apiFetch } from './apiClient';

type UploadPayload = {
  fileName: string;
  mimeType: string;
  base64: string;
};

type EvaluationProgress = {
  completed: number;
  total: number;
  phase: string;
};

type SubmitEvaluationResponse = {
  id: string;
  status: string;
  progress?: EvaluationProgress;
};

type EvaluationStatusResponse = {
  id: string;
  status: string;
  error_message?: string | null;
  progress?: EvaluationProgress | null;
  reports?: StudentDiagnosticReport[] | null;
};

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const toUploadPayload = (file: FileData): UploadPayload => ({
  fileName: file.file.name,
  mimeType: file.mimeType,
  base64: file.base64,
});

export async function submitEvaluationJob(
  files: RequiredFiles,
  gradeLevel: string,
  subject: string,
): Promise<string> {
  if (!files.questionPaper || !files.answerKey || !files.rubric || files.studentSheet.length === 0) {
    throw new Error('All required documents must be uploaded.');
  }

  const response = await apiFetch<SubmitEvaluationResponse>('/v1/evaluations', {
    method: 'POST',
    body: JSON.stringify({
      gradeLevel,
      subject,
      questionPaper: toUploadPayload(files.questionPaper),
      answerKey: toUploadPayload(files.answerKey),
      rubric: toUploadPayload(files.rubric),
      answerSheets: files.studentSheet.map(toUploadPayload),
    }),
  });

  return response.id;
}

export async function waitForEvaluationReports(
  evaluationId: string,
  onProgress?: (completed: number, total: number, phase: string) => void,
): Promise<StudentDiagnosticReport[]> {
  const terminalStatuses = new Set(['COMPLETED', 'FAILED']);

  while (true) {
    const status = await apiFetch<EvaluationStatusResponse>(`/v1/evaluations/${evaluationId}`);

    if (status.progress && onProgress) {
      onProgress(status.progress.completed, status.progress.total, status.progress.phase);
    }

    if (status.status === 'COMPLETED') {
      if (!status.reports || status.reports.length === 0) {
        throw new Error('Evaluation completed without reports.');
      }
      return status.reports;
    }

    if (status.status === 'FAILED') {
      throw new Error(status.error_message || 'Evaluation failed on the server.');
    }

    if (!terminalStatuses.has(status.status)) {
      await sleep(2500);
      continue;
    }

    await sleep(2500);
  }
}

export async function gradeWithBackend(
  files: RequiredFiles,
  gradeLevel: string,
  subject: string,
  onProgress?: (completed: number, total: number, phase: string) => void,
): Promise<StudentDiagnosticReport[]> {
  const evaluationId = await submitEvaluationJob(files, gradeLevel, subject);
  return waitForEvaluationReports(evaluationId, onProgress);
}
